/** Unformatted base-ten values; decimal strings retain precision beyond JS numbers. */
export type NxTableSummaryValue = number | string | null;
export type NxTableSummary<T> = 'sum' | 'none' | ((rows: readonly T[]) => NxTableSummaryValue);
export interface NxTableSummaryFormatContext<T> {
  rows: readonly T[];
  locale: string;
  unit: string | null;
}
export interface NxTableSummaryColumn<T> {
  summary?: NxTableSummary<T>;
  summaryValue?: (row: T, index: number) => NxTableSummaryValue | undefined;
  summaryFormat?: (value: NxTableSummaryValue, context: NxTableSummaryFormatContext<T>) => string;
  /** Prefix a non-zero total with `+`/`-`. */
  summaryShowSign?: boolean;
  /** Ink a non-zero total by its direction: positive green, negative red. */
  summarySignTone?: boolean;
  summaryUnit?: (row: T) => string | null | undefined;
}
export type NxTableSummaryState =
  | 'none'
  | 'value'
  | 'unavailable'
  | 'loading'
  | 'error'
  | 'incomplete'
  | 'invalid'
  | 'incompatible-unit';
export interface NxTableSummaryCellSlotProps {
  value: NxTableSummaryValue;
  text: string;
  columnKey: string;
  state: NxTableSummaryState;
}
export interface NxTableSummaryResult extends NxTableSummaryCellSlotProps {
  /** The direction the cell is inked by; null without `summarySignTone`. */
  sign: 'positive' | 'negative' | null;
}

type Decimal = { coefficient: bigint; scale: number };
/** Bound exponent expansion to avoid allocating unbounded strings from malformed input. */
function decimal(value: unknown): Decimal | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(String(value));
  if (!match) return null;
  const exponent = Number(match[4] ?? 0);
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10000) return null;
  let coefficient = BigInt(`${match[1] === '-' ? '-' : ''}${match[2]}${match[3] ?? ''}`);
  let scale = (match[3]?.length ?? 0) - exponent;
  if (scale < 0) {
    coefficient *= 10n ** BigInt(-scale);
    scale = 0;
  }
  return { coefficient, scale };
}
function decimalString({ coefficient, scale }: Decimal): string {
  const negative = coefficient < 0n;
  const digits = (negative ? -coefficient : coefficient).toString().padStart(scale + 1, '0');
  const text = scale
    ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}`.replace(/\.?0+$/, '')
    : digits;
  return `${negative ? '-' : ''}${text || '0'}`;
}

/** Formats exact decimals, rounding only here, half away from zero. */
export function formatSummaryDecimal(
  value: NxTableSummaryValue,
  locale: string,
  options: Intl.NumberFormatOptions = {}
): string {
  const parsed = decimal(value);
  if (!parsed) return '—';
  const negative = parsed.coefficient < 0n;
  let coefficient = negative ? -parsed.coefficient : parsed.coefficient;
  let scale = parsed.scale;
  const maximum = options.maximumFractionDigits ?? scale;
  const minimum = options.minimumFractionDigits ?? 0;
  if (scale > maximum) {
    const divisor = 10n ** BigInt(scale - maximum);
    coefficient = coefficient / divisor + ((coefficient % divisor) * 2n >= divisor ? 1n : 0n);
    scale = maximum;
  }
  let digits = coefficient.toString().padStart(scale + 1, '0');
  let fraction = scale ? digits.slice(-scale) : '';
  digits = scale ? digits.slice(0, -scale) : digits;
  fraction = fraction.replace(/0+$/, '').padEnd(minimum, '0');
  // Intl handles grouping, currency placement, signs and localized digits. Feed
  // only the exact integer BigInt, then replace the fraction without a float.
  const integer = BigInt(digits);
  const signed = negative ? (integer === 0n ? -0 : -integer) : integer;
  const formatter = new Intl.NumberFormat(locale, {
    ...options,
    minimumFractionDigits: fraction.length ? 1 : 0,
    maximumFractionDigits: fraction.length ? 1 : 0,
  });
  const digitFormatter = new Intl.NumberFormat(locale, { useGrouping: false });
  const localizedFraction = [...fraction]
    .map((digit) => digitFormatter.format(Number(digit)))
    .join('');
  return formatter
    .formatToParts(signed)
    .map((part) => (part.type === 'fraction' ? localizedFraction : part.value))
    .join('');
}

export function summaryDataIsComplete<T>(options: {
  rows: readonly T[];
  server: boolean;
  total?: number;
  complete?: boolean;
  placeholder?: (row: T) => boolean;
}): boolean {
  const { rows, server, total, complete, placeholder } = options;
  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    if (!(index in rows) || row == null || placeholder?.(row)) return false;
  }
  if (complete !== undefined) return complete;
  return !server || (total !== undefined && total >= 0 && rows.length === total);
}

export function resolveTableSummary<T extends Record<string, unknown>>(options: {
  rows: readonly T[];
  column: NxTableSummaryColumn<T> & { columnId: string };
  values?: Record<string, NxTableSummaryValue>;
  complete: boolean;
  loading?: boolean;
  error?: boolean;
  locale: string;
}): NxTableSummaryResult {
  const { rows, column, values, complete, loading, error, locale } = options;
  const empty = (state: NxTableSummaryState): NxTableSummaryResult => ({
    value: null,
    text: state === 'none' ? '' : '—',
    columnKey: column.columnId,
    state,
    sign: null,
  });
  if (!column.summary || column.summary === 'none') return empty('none');
  if (loading) return empty('loading');
  if (error) return empty('error');
  const external = !!values && Object.hasOwn(values, column.columnId);
  if (!external && !complete) return empty('incomplete');
  let unit: string | null = null;
  const units = new Set<string | null>();
  let sum: Decimal | null = null;
  if (!external) {
    // Only `sum` reads the cells as numbers. A custom function decides on the
    // rows itself (a count in a text column); its units are still checked.
    const summing = column.summary === 'sum';
    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      if (!summing) {
        if (row != null && column.summaryUnit) units.add(column.summaryUnit(row)?.trim() || null);
        continue;
      }
      const raw = column.summaryValue ? column.summaryValue(row, index) : row[column.columnId];
      if (raw == null) continue;
      const numeric = decimal(raw);
      if (!numeric) return empty('invalid');
      if (column.summaryUnit) units.add(column.summaryUnit(row)?.trim() || null);
      if (!sum) sum = numeric;
      else {
        const scale = Math.max(sum.scale, numeric.scale);
        sum = {
          coefficient:
            sum.coefficient * 10n ** BigInt(scale - sum.scale) +
            numeric.coefficient * 10n ** BigInt(scale - numeric.scale),
          scale,
        };
      }
    }
    if (units.size > 1) return empty('incompatible-unit');
    unit = units.values().next().value ?? null;
  } else if (column.summaryUnit) {
    // A business total is authoritative even for heterogeneous rows. Supply a
    // common unit to its formatter only when the available rows agree.
    for (const row of rows) if (row != null) units.add(column.summaryUnit(row)?.trim() || null);
    if (units.size === 1) unit = units.values().next().value ?? null;
  }
  const value = external
    ? values![column.columnId]!
    : typeof column.summary === 'function'
      ? column.summary(rows)
      : sum
        ? decimalString(sum)
        : null;
  if (value === null) return empty('unavailable');
  const parsed = decimal(value);
  if (!parsed) return empty('invalid');
  const direction =
    parsed.coefficient === 0n ? null : parsed.coefficient > 0n ? 'positive' : 'negative';
  const shownSign = column.summaryShowSign ? direction : null;
  let text = column.summaryFormat
    ? column.summaryFormat(value, { rows, locale, unit })
    : formatSummaryDecimal(
        value,
        locale,
        shownSign === 'positive' ? { signDisplay: 'always' } : {}
      );
  // A numeric sign starts a token, including after a currency symbol/label.
  // A hyphen attached to a word or number (2026-10, A-12) is label punctuation.
  if (shownSign && !/(?:^|[^\p{L}\p{M}\p{N}_])[+−-][\s\p{Sc}\p{Cf}]*\p{Nd}/u.test(text))
    text = `${shownSign === 'positive' ? '+' : '-'}${text}`;
  const sign = column.summarySignTone ? direction : null;
  return { value, text, columnKey: column.columnId, state: 'value', sign };
}
